import * as z from 'zod';
import client from '../client';

import { Media, CTA } from '../strapiSchema';
import { isNotFoundError, logMissingResource, PRIMARY_LOCALE } from '../strapiLocale';

const Header = z.object({
  id: z.number(),
  documentId: z.string(),
  logoWhite: Media,
  logoBlack: Media,
  logoMobile: Media,
  menu: z.array(z.object({
    linkType: z.enum(['Link', 'Dropdown']),
    link: CTA,
    dropdown: z.array(CTA).nullish(),
  })),
  loginButton: CTA,
  registerButton: CTA,
  support: z.object({
    title: z.string(),
    links: z.array(z.object({
      title: z.string().nullish(),
      icon: Media.nullable().optional(),
      url: z.string(),
    })),
  }).nullish(),
});

export type HeaderType = z.infer<typeof Header>;

export const getHeader = async (
  locale: string = PRIMARY_LOCALE,
): Promise<HeaderType | null> => {
  try {
    const response = await client.get('/api/header', {
      params: {
        locale,
        populate: [
          'logoWhite',
          'logoBlack',
          'logoMobile',
          'menu.link',
          'menu.dropdown',
          'loginButton',
          'registerButton',
          'support.links.icon',
        ],
      },
    });

    if (!response.data?.data) {
      if (locale !== PRIMARY_LOCALE) {
        logMissingResource('header', locale);
        return null;
      }
      throw new Error('No header found');
    }

    return Header.parseAsync(response.data.data);
  } catch (error) {
    if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
      logMissingResource('header', locale);
      return null;
    }

    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`API response do not match Header structure : ${errorMessage}`);
  }
};
